<template>
    <div v-if="isStandAloneTreatment" class="ls-order-summary">
        <div v-if="lawnTreatmentIsInCart" class="ls-order-summary__item ls-margin--bottom-none">
            <div class="ls-order-summary__item-description">
                <strong
                    data-font-size="-1"
                    :class="{ 'font-weight--500': isLawnLove || isHomeGnome }"
                    test-id="monthly-treatment"
                >
                    Monthly Treatment
                </strong>
                <br />
                <span class="ls-color--gray">Starting {{ order.start_date | lsDate }}</span>
            </div>

            <div
                class="ls-order-summary__item-price ls-margin--bottom"
                :class="{
                    'mute-display': discountAmount,
                }"
            >
                <!-- br trailing strong tag to avoid errant spaces when rendering -->
                <strong class="ls-order-summary__item-price-amount" test-id="total-treatment">{{
                    nonMowingServicesInCart[0].price.price | lsMoney
                }}</strong>
                <br />
                <span
                    class="ls-order-summary__item-price-secondary"
                    data-font-size="-3"
                    test-id="per-treatment"
                >
                    per treatment
                </span>
            </div>

            <div class="ls-order-summary__total ls-margin--top">
                <span class="ls-order-summary__total-label">Total</span>
                <span class="ls-order-summary__total-value">
                    <span v-if="!cart.loadingStatus.calculatingTotal">
                        {{ treatmentTotal | lsMoney }}
                    </span>
                    <span v-else>
                        <i class="fa fa-pulse fa-spinner"></i>
                    </span>
                </span>
            </div>
        </div>
    </div>
    <div v-else class="ls-order-summary">
        <div v-if="mowingInCart" class="ls-order-summary__item ls-margin--bottom-none">
            <div class="ls-order-summary__item-description">
                <strong
                    data-font-size="-1"
                    :class="{ 'font-weight--500': isLawnLove || isHomeGnome }"
                >
                    {{ mowingInCart.price.cycle | lsCycle }}
                    {{ mowingInCart.price.service.name | lsServiceName }}
                </strong>
                <br />
                <span class="ls-color--gray">Starting {{ order.start_date | lsDate }}</span>
            </div>

            <div
                class="ls-order-summary__item-price"
                :class="{
                    'mute-display': discountAmount,
                }"
            >
                <!-- br trailing strong tag to avoid errant spaces when rendering -->
                <strong class="ls-order-summary__item-price-amount">{{
                    mowingPricePerService | lsMoney
                }}</strong>
                <br />
                <span class="ls-order-summary__item-price-secondary" data-font-size="-3">
                    per service
                </span>
            </div>

            <div v-if="discountAmount" class="ls-order-summary__discount">
                <span class="ls-order-summary__discount-label">Discount</span>
                <span class="ls-order-summary__discount-value"
                    >&ndash;{{ discountAmount | lsMoney }}</span
                >
            </div>

            <div
                v-if="order.trustfee"
                class="ls-order-summary__trust-fee"
                :class="{ 'ls-color--gray': isLawnLove || isHomeGnome }"
                test-id="trust-fee"
            >
                <!-- tabindex required for tooltip to function on click on iOS -->
                <span
                    tabindex="0"
                    :data-balloon-length="screenSize.xs ? 'medium' : 'large'"
                    data-balloon-pos="down"
                    :aria-label="`Our Trust & Safety fee helps cover ${brand}'s ongoing review of the business information and licensing (if any licenses are required by local laws) of the lawn care providers you engage through the platform.`"
                >
                    Trust &amp;
                    <span class="ls-nowrap">
                        Safety Fee
                        <i
                            class="fa fa-fw fa-question-circle"
                            :class="{ 'ls-color--blue': isLawnLove || isHomeGnome }"
                        />
                    </span>
                </span>
                <span>{{ order.trustfee | lsMoney }}</span>
            </div>

            <div class="ls-order-summary__total">
                <span class="ls-order-summary__total-label">{{ mowingPerServiceText }}</span>
                <span
                    class="ls-order-summary__total-value"
                    :class="{
                        'discounted-price': discountAmount,
                    }"
                >
                    <!-- no whitespace
                    -->
                    <span v-if="!cart.loadingStatus.calculatingTotal">{{
                        mowingTotalPricePerService | lsMoney
                    }}</span>
                    <!-- no whitespace
                    -->
                    <span v-else>
                        <i class="fa fa-pulse fa-spinner"></i>
                    </span>
                    <!-- no whitespace
                    -->
                </span>
            </div>
        </div>

        <div v-if="showAdditionalServices" class="ls-order-summary__additional-services">
            <div
                v-for="orderService in nonMowingServicesInCart"
                :key="orderService.price_id"
                class="ls-order-summary__item"
            >
                <div
                    class="ls-order-summary__item-description ls-order-summary__item-description-with-icon"
                >
                    <branded-checkbox :is-active="true" :is-big-icon="true" />
                    <span
                        class="ls-bold"
                        :class="{
                            'll-color--black--light font-weight--400': isLawnLove || isHomeGnome,
                        }"
                        data-font-size="-1"
                        >{{ orderService.price.service.name }}</span
                    >
                </div>
                <div class="ls-order-summary__item-price">
                    <span
                        class="ls-italic"
                        :class="{
                            'll-color--black--light font-weight--400': isLawnLove || isHomeGnome,
                            'ls-color--gray--dark': isLawnStarter,
                        }"
                        data-font-size="-1"
                        >{{ orderService.price.price | lsMoney }} per service</span
                    >
                </div>
            </div>

            <div
                v-for="orderQuote in allQuotes"
                :key="orderQuote.service_id"
                class="ls-order-summary__item"
            >
                <div
                    class="ls-order-summary__item-description ls-order-summary__item-description-with-icon"
                >
                    <branded-checkbox :is-active="true" :is-big-icon="true" />
                    <span
                        class="ls-bold"
                        :class="{
                            'll-color--black--light font-weight--400': isLawnLove || isHomeGnome,
                        }"
                        data-font-size="-1"
                        >{{ orderQuote.name }}</span
                    >
                </div>
                <div class="ls-order-summary__item-price">
                    <span
                        class="ls-italic"
                        :class="{
                            'll-color--black--light font-weight--400': isLawnLove || isHomeGnome,
                            'ls-color--gray--dark': isLawnStarter,
                        }"
                        data-font-size="-1"
                        >TBD</span
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';
import * as types from '@/store/types';
import brandMixin from '@/mixins/brand.mixin';
import brandedCheckbox from '@/components/original-mydot/branded-checkbox/BrandedCheckbox.vue';
import { serviceType } from '@/constants';

export default {
    components: {
        brandedCheckbox,
    },
    mixins: [brandMixin],
    computed: {
        ...mapState(['cart', 'order', 'screenSize']),
        ...mapGetters({
            mowingInCart: types.CART__GET_MOWING_IN_CART,
            nonMowingServicesInCart: types.CART__GET_NON_MOWING_SERVICES_IN_CART,
            lawnTreatmentIsInCart: types.CART__GET_PRICED_LAWNTREATMENT_IS_IN_CART,
            cartTotals: types.ORDER__GET_CART_TOTALS,
            discountAmount: types.ORDER__GET_DISCOUNT_AMOUNT,
            allQuotes: types.CART__GET_QUOTES_AND_INSTANT_QUOTES_SERVICE_OBJECTS_IN_CART,
            treatmentTotal: types.ORDER__GET_CART_TREATMENT_TOTAL,
        }),

        mowingPricePerService() {
            return _.get(this.mowingInCart, 'price.price');
        },

        mowingTotalPricePerService() {
            return _.get(this.cartTotals, 'total_amount');
        },

        mowingPerServiceText() {
            return this.discountAmount ? 'First Service' : 'Total';
        },

        showAdditionalServices() {
            return _.size(this.nonMowingServicesInCart) || _.size(this.allQuotes);
        },
        isStandAloneTreatment() {
            return serviceType.TREATMENT === this.order.intent;
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-order-summary__item {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    // set to smallest text included so line height on larger text doesn't mess with spacing
    font-size: rem(-2);

    &:not(:last-of-type) {
        margin-bottom: rem(-5);
    }

    > .ls-order-summary__item-description {
        flex: 1 1 auto;
    }

    > .ls-order-summary__item-price {
        flex: 0 0 auto;
        text-align: right;
        color: palette(green, color-dodge);
        font-weight: 600;
        line-height: 1 !important;

        strong {
            font-weight: 700;
        }

        .ls-order-summary__item-price-amount {
            font-size: rem(3);
        }

        .ls-order-summary__item-price-secondary {
            font-size: rem(-3);
        }

        &.mute-display {
            color: palette(gray, dark);
            .ls-order-summary__item-price-amount {
                font-size: rem(1);
            }

            .ls-order-summary__item-price-secondary {
                font-size: rem(-1);
            }
        }
    }

    .ls-order-summary__item-description-with-icon {
        // for managing checkbox displays from mydot
        display: flex;
        align-items: center;
    }
}

.ls-order-summary__discount {
    display: flex;
    justify-content: space-between;
    flex: 0 0 100%;
    margin-top: rem(-5);
    color: palette(gray);
}

.ls-order-summary__trust-fee {
    display: flex;
    justify-content: space-between;
    flex: 0 0 100%;
    margin-top: rem(-5);
    color: palette(gray);
    :focus {
        outline: none;
    }
}

.ls-order-summary__total {
    display: flex;
    flex: 0 0 100%;
    justify-content: space-between;
    align-items: center;
    margin-top: rem(-5);
    padding-top: rem(-5);
    border-top: 1px solid palette(gray, x-light);
}

.ls-order-summary__total-label {
    font-size: rem(-1);
    font-weight: 700;
}

.ls-order-summary__total-value {
    font-size: rem(-1);
    font-weight: 700;
    &.discounted-price {
        color: palette(green, color-dodge);
        font-size: rem(3);
    }
}

.ls-order-summary__additional-services {
    margin-top: rem(-5);
    padding-top: rem(-5);
    border-top: 1px solid palette(gray, x-light);
}
.themed-app {
    .ls-order-summary__item {
        > .ls-order-summary__item-price {
            color: $theme-color-primary;

            &.mute-display {
                color: $theme-color-gray-darker;
            }
        }
    }

    .ls-order-summary__discount {
        color: $theme-color-gray-darker;
    }

    .ls-order-summary__trust-fee {
        color: $theme-color-gray-darker;
    }

    .ls-order-summary__total {
        border-color: $theme-color-gray;
    }

    .ls-order-summary__total-value {
        &.discounted-price {
            color: $theme-color-primary;
        }
    }

    .ls-order-summary__additional-services {
        border-color: $theme-color-gray;
    }
}
</style>
<style lang="scss">
.ls-order-summary__item {
    .ls-order-summary__item-description-with-icon i {
        padding-right: rem(-5);
    }
}

.themed-lawnlove {
    .ls-order-summary__total {
        .ls-order-summary__total-value {
            font-weight: 500;
        }

        .ls-order-summary__total-label {
            font-weight: 500;
        }
    }

    .ls-order-summary__item-description-with-icon i {
        margin: 0.2em 0.5em 0.2em 0;
    }
}
</style>
